import { createFileRoute } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import {
  AgencySalonPage,
  DEFAULT_AGENCY_SALON_TAB,
  getOrganizationName,
  parseAgencySalonSearch,
  useAgencySalonDetail,
  type AgencySalonTab,
} from '@/features/agency';
import { formatDocumentTitle } from '@/lib/document-title';

/**
 * Agency-only salon page: features on/off, plan and what the salon may do, one tab at a time (`?tab=plan`). The agency
 * layout already refuses non-agency users; the page itself shows "not found" for a salon outside the viewer's scope.
 */
export const Route = createFileRoute('/_auth/agency/salons/$shortCode')({
  validateSearch: parseAgencySalonSearch,
  component: AgencySalonRoute,
});

function AgencySalonRoute() {
  const { shortCode } = Route.useParams();
  const { tab = DEFAULT_AGENCY_SALON_TAB } = Route.useSearch();
  const navigate = Route.useNavigate();
  // Same query as the page (deduped): titles the tab once the salon loads; the page handles loading / not found.
  const { salon } = useAgencySalonDetail(shortCode);

  const changeTab = (next: AgencySalonTab) =>
    void navigate({
      search: (prev) => ({ ...prev, tab: next === DEFAULT_AGENCY_SALON_TAB ? undefined : next }),
      // Same page, another tab: stay where the reader is.
      resetScroll: false,
    });

  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle(salon ? getOrganizationName(salon) : 'Salon')}</title>
      </Helmet>
      {/* Keyed by salon: per-salon state (an open confirm or plan dialog) never carries over to another salon. */}
      <AgencySalonPage key={shortCode} shortCode={shortCode} tab={tab} onTabChange={changeTab} />
    </>
  );
}
